import React, { useState, useEffect, useCallback } from 'react';
import { Box, Slide, AppBar, useScrollTrigger, Container, Fade } from '@mui/material';

const EnhancedHeader = ({ TopHeader, Header }) => {
    const [showHeader, setShowHeader] = useState(false);
    const [mouseInHeader, setMouseInHeader] = useState(false);
    const trigger = useScrollTrigger();

    const debounce = (func, wait) => {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    };

    const handleMouseMove = useCallback(
        debounce((e) => {
            const triggerArea = 100;
            const isInTriggerArea = e.clientY < triggerArea;

            if (isInTriggerArea || mouseInHeader) {
                setShowHeader(true);
            } else {
                if (!mouseInHeader) {
                    setShowHeader(false);
                }
            }
        }, 50),
        [mouseInHeader],
    );

    const handleMouseEnter = () => {
        setMouseInHeader(true);
        setShowHeader(true);
    };

    const handleMouseLeave = () => {
        setMouseInHeader(false);
        if (window.event.clientY >= 100) {
            setShowHeader(false);
        }
    };

    useEffect(() => {
        window.addEventListener('mousemove', handleMouseMove);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
        };
    }, [handleMouseMove]);

    return (
        <Slide direction="down" in={showHeader && !trigger} mountOnEnter unmountOnExit timeout={400}>
            <AppBar
                position="fixed"
                sx={{
                    top: 0,
                    backgroundColor: '#ffffff',
                    boxShadow: 'rgba(0, 0, 0, 0.06) 0px 4px 20px',
                    zIndex: 9999,
                    color: 'text.primary',
                    transition: 'all 0.3s ease',
                    '& .MuiToolbar-root': {
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 0',
                    },
                    '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.1) 0px 8px 24px',
                    }
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                role="banner"
                aria-label="Navigation header"
                elevation={0}
            >
                <Fade in={true} timeout={800}>
                    <Box
                        className="test-page-header"
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            backgroundColor: '#ffffff',
                        }}
                    >
                        {/* TopHeader section with explicit text color */}
                        <Box
                            sx={{
                                background: 'linear-gradient(90deg, #f8f8f8 0%, #efefef 100%)',
                                color: 'text.primary',
                                boxShadow: 'inset 0 -1px 0 rgba(255,255,255,0.3)',
                                '& .MuiTypography-root': {
                                    color: 'inherit',
                                },
                            }}
                        >
                            <Container maxWidth="xxl">
                                <TopHeader />
                            </Container>
                        </Box>

                        {/* Main Header section */}
                        <Box
                            sx={{
                                backgroundColor: '#fff',
                                borderBottom: '1px solid rgba(208, 208, 208, 0.5)',
                                boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                                '& .MuiToolbar-root > div:last-child': {
                                    display: 'flex !important',
                                    alignItems: 'center',
                                    gap: '56px',
                                },
                                '& .MuiButton-root': {
                                    color: '#333',
                                    fontWeight: 600,
                                    borderRadius: '8px',
                                    '&:hover': {
                                        color: '#ff5e1c',
                                        backgroundColor: 'rgba(255,94,28,0.08)',
                                        transition: 'all 0.3s ease',
                                    },
                                },
                                '& .MuiTypography-root': {
                                    color: '#333',
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                    position: 'relative',
                                    padding: '6px 0',
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        height: '2px',
                                        backgroundColor: '#ff5e1c',
                                        transform: 'scaleX(0)',
                                        transformOrigin: 'bottom right',
                                        transition: 'transform 0.3s ease',
                                        borderRadius: '1px',
                                    },
                                    '&:hover': {
                                        color: '#ff5e1c',
                                        transition: 'all 0.3s ease',
                                    },
                                    '&:hover::after': {
                                        transform: 'scaleX(1)',
                                        transformOrigin: 'bottom left',
                                    },
                                },
                                '& img': {
                                    height: 60,
                                    width: 'auto',
                                    marginRight: '20px',
                                    transition: 'transform 0.4s ease, filter 0.3s ease',
                                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                                    '&:hover': {
                                        transform: 'scale(1.05) translateY(-2px)',
                                        filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.15))'
                                    },
                                },
                                '& .MuiIconButton-root': {
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                    }
                                },
                                '& .MuiBadge-badge': {
                                    fontWeight: 'bold'
                                }
                            }}
                        >
                            <Container maxWidth="xxl">
                                <Header />
                            </Container>
                        </Box>
                    </Box>
                </Fade>
            </AppBar>
        </Slide>
    );
};

export default EnhancedHeader;
